import { brand } from "../../../config/theme";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import Swal from "sweetalert2";
import api from "../../../utils/api";
import { LogoSplash } from "../../../config/brandAssets";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

type ProcessState =
  | "loading"
  | "validating"
  | "form"
  | "submitting"
  | "success"
  | "error";

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("passwordReset");
  const token = searchParams.get("token");
  const hasValidated = useRef(false);
  const [processState, setProcessState] = useState<ProcessState>("loading");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>({ mode: "onBlur" });

  const newPassword = watch("newPassword");

  // Validar token al cargar la página
  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;

    const validateToken = async () => {
      if (!token) {
        await Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("invalidOrMissingToken"),
          confirmButtonText: t("goToLogin"),
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        navigate("/");
        return;
      }

      try {
        setProcessState("validating");

        const response = await api.post("/auth/validate-password-reset-token", {
          token,
        });

        const email = response.data?.data?.data?.email || "";
        setUserEmail(email);
        setProcessState("form");
      } catch (error: unknown) {
        setProcessState("error");

        let errorMessage = t("unknownError");

        if (error && typeof error === "object" && "response" in error) {
          const axiosError = error as {
            response?: {
              data?: {
                meta?: { message?: string };
              };
            };
          };

          const message = axiosError.response?.data?.meta?.message;

          if (message) {
            if (
              message.includes("ya fue utilizado") ||
              message.includes("already been used")
            ) {
              errorMessage = t("linkAlreadyUsed");
            } else if (
              message.includes("expirado") ||
              message.includes("expired") ||
              message.includes("expiró")
            ) {
              errorMessage = t("linkExpired");
            } else if (
              message.includes("inválido") ||
              message.includes("invalid") ||
              message.includes("no encontrado") ||
              message.includes("not found")
            ) {
              errorMessage = t("invalidLink");
            } else {
              errorMessage = message;
            }
          }
        }

        await Swal.fire({
          icon: "error",
          title: t("error"),
          text: errorMessage,
          confirmButtonText: t("goToLogin"),
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        navigate("/");
      }
    };

    validateToken();
  }, [token, navigate, t]);

  const onSubmit: SubmitHandler<ResetPasswordForm> = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      await Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("passwordsDoNotMatch"),
        confirmButtonText: t("tryAgain"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    setProcessState("submitting");

    try {
      await api.post("/auth/execute-password-reset", {
        token,
        newPassword: data.newPassword,
      });

      setProcessState("success");

      await Swal.fire({
        icon: "success",
        title: t("success"),
        text: t("passwordChangedSuccessfully"),
        confirmButtonText: t("goToLogin"),
        confirmButtonColor: brand.primary,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      navigate("/");
    } catch (error: unknown) {
      setProcessState("error");

      let errorMessage = t("unknownError");

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: {
            data?: {
              meta?: { message?: string };
            };
          };
        };

        const message = axiosError.response?.data?.meta?.message;

        if (message) {
          if (
            message.includes("ya fue utilizado") ||
            message.includes("already been used")
          ) {
            errorMessage = t("linkAlreadyUsed");
          } else if (
            message.includes("expirado") ||
            message.includes("expired")
          ) {
            errorMessage = t("linkExpired");
          } else if (message.includes("corta") || message.includes("short")) {
            errorMessage = t("passwordTooShort");
          } else {
            errorMessage = message;
          }
        }
      }

      await Swal.fire({
        icon: "error",
        title: t("error"),
        text: errorMessage,
        confirmButtonText: t("tryAgain"),
        confirmButtonColor: brand.primary,
      });

      setProcessState("form");
    }
  };

  const getStateContent = () => {
    switch (processState) {
      case "loading":
      case "validating":
        return {
          title: t("pageTitle"),
          subtitle: t("validatingLink"),
          description: t("pleaseWait"),
          showSpinner: true,
          showForm: false,
        };

      case "form":
        return {
          title: t("pageTitle"),
          subtitle: t("enterNewPassword"),
          description: userEmail ? t("forAccount", { email: userEmail }) : "",
          showSpinner: false,
          showForm: true,
        };

      case "submitting":
        return {
          title: t("pageTitle"),
          subtitle: t("changingPassword"),
          description: t("pleaseWait"),
          showSpinner: true,
          showForm: false,
        };

      case "success":
        return {
          title: t("success"),
          subtitle: t("passwordChangedSuccessfully"),
          description: t("canLoginNow"),
          showSpinner: false,
          showForm: false,
          showIcon: true,
          icon: "success",
        };

      case "error":
        return {
          title: t("error"),
          subtitle: t("errorOccurred"),
          description: t("tryAgainLater"),
          showSpinner: false,
          showForm: false,
          showIcon: true,
          icon: "error",
        };

      default:
        return {
          title: t("pageTitle"),
          subtitle: "",
          description: t("pleaseWait"),
          showSpinner: true,
          showForm: false,
        };
    }
  };

  const stateContent = getStateContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <img src={LogoSplash} alt="Logo" className="w-32 h-32 mx-auto" />
        </div>

        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {stateContent.title}
        </h1>

        <p className="text-gray-600 mb-6">{stateContent.subtitle}</p>

        {stateContent.showSpinner && (
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {stateContent.showIcon && (
          <div className="flex justify-center mb-4">
            {stateContent.icon === "success" ? (
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </div>
            )}
          </div>
        )}

        {stateContent.showForm && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="text-left">
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("newPassword")}
              </label>
              <div className="relative">
                <input
                  {...register("newPassword", {
                    required: t("requiredField"),
                    minLength: {
                      value: 6,
                      message: t("passwordTooShort"),
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all ${
                    errors.newPassword ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("enterNewPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="text-left">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("confirmPassword")}
              </label>
              <div className="relative">
                <input
                  {...register("confirmPassword", {
                    required: t("requiredField"),
                    validate: (value) =>
                      value === newPassword || t("passwordsDoNotMatch"),
                  })}
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all ${
                    errors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder={t("confirmNewPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  <FontAwesomeIcon
                    icon={showConfirmPassword ? faEye : faEyeSlash}
                  />
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-primary-500 hover:bg-[#5FE838] text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              {t("changePassword")}
            </button>
          </form>
        )}

        <p className="text-sm text-gray-500 mt-4">{stateContent.description}</p>

        {(processState === "success" || processState === "error") && (
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-4 bg-primary-500 hover:bg-[#5FE838] text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            {t("goToLogin")}
          </button>
        )}
      </div>
    </div>
  );
}
